import { GrowthTypes } from '@ecosystem/formatters';
import {
  defaultItemFeature,
  GroupState,
  Item,
  ItemFeaturesResp,
  LeaderboardKpi,
  ProductAnalyzerGroupDetails,
  SaleType,
} from '@models/Products';
import { FocusArea } from '@components/products-top-5-table/Types';
import { DisplayOptions } from '../products-table-filter/Types';
import ProductsTableRow from '../products-table-row/ProductsTableRow';
import { useState } from 'react';
import { ColumnConfig, TableBody } from '@components/design/migrated/Table';
import * as styles from '../ProductsTable.module.scss';
import { useProductAnalyzerGroupEventState } from '../product-table-grouping/utils/ProductAnalyzerGroupContext';
import classnames from 'classnames';
import { getKpiWithMarketShare } from '@components/products-top-5-table/utils/ProductsUtils';
import { DELETE_GROUP_PG_ELEMENT_NAME, GROUP_ELEMENT_TYPE, GroupContext } from '@utils/userPreferencesUtils/Types';
import {
  groupElementClickedTrackingFunction,
  PRODUCT_LEADERBOARD_TABLE_ELEMENT_CLICKED_TITLE,
  PRODUCT_LEADERBOARD_TABLE_NAME,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import { useProductAnalyzerGroupState } from '@utils/hook/userPreferences/UseGroupState';
import { IRootState } from '@state/reducers/Reducers';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { isRetailerKpiZero } from '@components/top-selling-products/ProductsTable';

interface Props {
  columns: ColumnConfig[];
  groupDetails: ProductAnalyzerGroupDetails;
  leaderboardGroupKpi: LeaderboardKpi[];
  selectedDisplayType: DisplayOptions;
  selectedSaleType: SaleType;
  selectedFocusArea?: FocusArea;
  currency: string;
  selectedGrowthType: GrowthTypes;
  enableSort?: boolean;
  sortedColumn: string;
  itemFeatures?: ItemFeaturesResp[]; // only applicable to leaderboard tab
  context: GroupContext;
}

export const ProductsTableGroupRow: React.FC<Props> = ({
  columns,
  groupDetails,
  leaderboardGroupKpi,
  selectedDisplayType,
  selectedSaleType,
  selectedFocusArea = FocusArea.market,
  currency,
  selectedGrowthType,
  enableSort = true,
  sortedColumn,
  itemFeatures,
  context,
}) => {
  const { updateProductAnalyzerGroup } = useProductAnalyzerGroupState(context);
  const { isEditGroupMode, setIsEditGroupMode } = useProductAnalyzerGroupEventState();
  const [isRowExpanded, setIsRowExpanded] = useState<boolean>(false);
  const totalModelItem = 'TOTAL';
  const getleaderboardGroupTotalKpi: LeaderboardKpi | undefined = leaderboardGroupKpi.find(
    (leaderboardKpi) => leaderboardKpi.item.itemName === totalModelItem,
  );
  const getleaderboardGroupKpi: LeaderboardKpi[] = leaderboardGroupKpi.filter(
    (leaderboardKpi) => leaderboardKpi.item.itemName !== totalModelItem,
  );
  const getLeaderboardKpiWithGroupName = (): LeaderboardKpi => {
    const displayGroupNameWithPgsCount = `${groupDetails.groupName} (${getleaderboardGroupKpi.length})`;
    return {
      ...getleaderboardGroupTotalKpi!,
      item: {
        itemId: displayGroupNameWithPgsCount,
        itemBrand: displayGroupNameWithPgsCount,
      } as Item,
    };
  };
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);

  const isDisableGroups = () => {
    return isEditGroupMode.isEditMode && isEditGroupMode.groupDetails.groupName !== groupDetails.groupName;
  };
  const groupStateForGroupHeader: GroupState = {
    groupName: groupDetails.groupName,
    enableIsEditGroupMode: () =>
      setIsEditGroupMode({
        groupDetails: groupDetails,
        isEditMode: true,
      }),
    isGroupingHeader: true,
    isGroupEmpty: getleaderboardGroupKpi.length === 0,
    setIsGroupingRowExpanded: setIsRowExpanded,
    groupSize: getleaderboardGroupKpi.length,
    isPreferenceOwner: groupDetails.isPreferenceOwner,
  };
  const onDeleteGroupMgs = (mgToBeRemove: string, nametoBeRemove: string) => {
    //Amplitude Tracking when Remove pgs icon is clicked
    const trackingParam: TableElementClickedParam = {
      eventTitle: PRODUCT_LEADERBOARD_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: DELETE_GROUP_PG_ELEMENT_NAME.model,
      elementType: GROUP_ELEMENT_TYPE.model,
      track: track,
      optionChosen: nametoBeRemove,
      pageName: PageName.RI_PRODUCT,
      pageType: PageType.RI_PRODUCT,
      sideFilters: sideFiltersState.appliedFilters,
      tableName: PRODUCT_LEADERBOARD_TABLE_NAME,
      access: pagesConfig.access,
    };

    groupElementClickedTrackingFunction(trackingParam);
    updateProductAnalyzerGroup.mutate({
      key: groupDetails.groupName,
      data: {
        modalGroup: groupDetails.itemGroup.filter((mg) => mg !== mgToBeRemove),
        sortOrder: groupDetails.sortOrder!,
      },
    });
  };
  const groupStateForGroupBody: GroupState = {
    groupName: groupDetails.groupName,
    isGroupingModelItem: true,
    onDeleteGroupMgs: onDeleteGroupMgs,
    isPreferenceOwner: groupDetails.isPreferenceOwner,
  };
  const findItemFeature = (kpi: LeaderboardKpi) =>
    itemFeatures?.find((item: ItemFeaturesResp) => item.itemId === kpi.item.itemId);

  return (
    <TableBody>
      {getLeaderboardKpiWithGroupName && (
        <ProductsTableRow
          selectedDisplayType={selectedDisplayType}
          key={groupDetails.groupName}
          leaderboardKpi={getLeaderboardKpiWithGroupName()}
          columns={columns}
          itemFeature={defaultItemFeature}
          className={classnames([
            styles.ProductsTable__TableRow__WithHover,
            { [styles['TableViewboard__TableRow__isDisabled']]: isDisableGroups() },
          ])}
          currency={currency}
          isStickyRow={false}
          selectedGrowthType={selectedGrowthType}
          selectedSaleType={selectedSaleType}
          selectedFocusArea={selectedFocusArea}
          {...(enableSort && { sortedColumn: sortedColumn })}
          groupState={groupStateForGroupHeader}
        />
      )}
      {isRowExpanded &&
        !isEditGroupMode.isEditMode &&
        getleaderboardGroupKpi.map((kpiArg, index) => {
          let kpi = kpiArg;
          const itemFeature = findItemFeature(kpi);

          if (selectedDisplayType === DisplayOptions.trends) {
            kpi = {
              ...getKpiWithMarketShare(getleaderboardGroupKpi, kpi, selectedSaleType, selectedFocusArea),
            };
          }
          return (
            <ProductsTableRow
              selectedDisplayType={selectedDisplayType}
              key={`${groupDetails.groupName} ${index}`}
              rowIndex={index}
              leaderboardKpi={kpi}
              columns={columns}
              itemFeature={itemFeature ?? defaultItemFeature}
              className={classnames([styles.ProductsTable__TableRow__WithHover], {
                [styles.ProductsTable__TableRow__WithHover__RetailerKpiZero]: isRetailerKpiZero(kpi),
              })}
              currency={currency}
              isStickyRow={false}
              selectedGrowthType={selectedGrowthType}
              selectedSaleType={selectedSaleType}
              selectedFocusArea={selectedFocusArea}
              {...(enableSort && { sortedColumn: sortedColumn })}
              groupState={groupStateForGroupBody}
            />
          );
        })}
    </TableBody>
  );
};
